import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import {
  Users,
  FileText,
  TrendingUp,
  Euro,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
} from 'lucide-react'
import { useDashboardStats } from '@/hooks/useDashboardStats'
import { formatCurrency } from '@/lib/utils'
import { OverdueTasksWidget } from '@/components/admin/tasks/OverdueTasksWidget'
import { ActiveTasksWidget } from '@/components/admin/tasks/ActiveTasksWidget'

export default function DashboardPage() {
  const { stats, recentContacts, recentQuotes, loading, error } = useDashboardStats()

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p>Erreur lors du chargement des statistiques: {error}</p>
        </div>
      </div>
    )
  }

  const statsConfig = [
    {
      name: 'Total Clients',
      value: loading ? '-' : stats.totalClients.toString(),
      change: loading ? '0%' : `${stats.clientsChange > 0 ? '+' : ''}${stats.clientsChange.toFixed(1)}%`,
      changeType: stats.clientsChange >= 0 ? 'positive' as const : 'negative' as const,
      icon: Users,
    },
    {
      name: 'Devis en cours',
      value: loading ? '-' : stats.activeQuotes.toString(),
      change: loading ? '0%' : `${stats.quotesChange > 0 ? '+' : ''}${stats.quotesChange.toFixed(1)}%`,
      changeType: stats.quotesChange >= 0 ? 'positive' as const : 'negative' as const,
      icon: FileText,
    },
    {
      name: 'Taux de conversion',
      value: loading ? '-%' : `${stats.conversionRate.toFixed(1)}%`,
      change: loading ? '0%' : `${stats.conversionChange > 0 ? '+' : ''}${stats.conversionChange.toFixed(1)}%`,
      changeType: stats.conversionChange >= 0 ? 'positive' as const : 'negative' as const,
      icon: TrendingUp,
    },
    {
      name: 'CA potentiel',
      value: loading ? '- EUR' : formatCurrency(stats.potentialRevenue),
      change: loading ? '0%' : `${stats.revenueChange > 0 ? '+' : ''}${stats.revenueChange.toFixed(1)}%`,
      changeType: stats.revenueChange >= 0 ? 'positive' as const : 'negative' as const,
      icon: Euro,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-brown-900">
          Tableau de bord
        </h1>
        <p className="mt-1 text-brown-600">
          Bienvenue dans votre espace d'administration
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsConfig.map((stat) => (
          <Card key={stat.name}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-brown-500">{stat.name}</p>
                <p className="mt-2 text-2xl font-semibold text-brown-900">
                  {loading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-brown-300" />
                  ) : (
                    stat.value
                  )}
                </p>
              </div>
              <div className="p-2 bg-mint-50 rounded-lg">
                <stat.icon className="w-5 h-5 text-mint-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              {stat.changeType === 'positive' ? (
                <ArrowUpRight className="w-4 h-4 text-mint-600" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-red-600" />
              )}
              <span
                className={
                  stat.changeType === 'positive'
                    ? 'text-mint-600'
                    : 'text-red-600'
                }
              >
                {stat.change}
              </span>
              <span className="ml-2 text-brown-500">vs mois dernier</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent contacts */}
        <Card>
          <CardHeader>
            <CardTitle>Contacts récents</CardTitle>
            <Badge variant="mint">{loading ? '-' : recentContacts.length} total</Badge>
          </CardHeader>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-brown-300" />
            </div>
          ) : recentContacts.length === 0 ? (
            <div className="text-center py-8 text-brown-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-beige-300" />
              <p>Aucun contact pour le moment</p>
              <p className="text-sm mt-1">
                Les nouveaux contacts apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="divide-y divide-beige-200">
              {recentContacts.map((contact) => (
                <div key={contact.id} className="px-6 py-4 hover:bg-beige-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-brown-900">
                        {contact.full_name || contact.email}
                      </p>
                      {contact.company && (
                        <p className="text-sm text-brown-500">{contact.company}</p>
                      )}
                    </div>
                    <p className="text-sm text-brown-500">
                      {new Date(contact.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent quotes */}
        <Card>
          <CardHeader>
            <CardTitle>Devis récents</CardTitle>
            <Badge variant="gold">{loading ? '-' : recentQuotes.length} total</Badge>
          </CardHeader>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-brown-300" />
            </div>
          ) : recentQuotes.length === 0 ? (
            <div className="text-center py-8 text-brown-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-beige-300" />
              <p>Aucun devis pour le moment</p>
              <p className="text-sm mt-1">
                Les nouveaux devis apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="divide-y divide-beige-200">
              {recentQuotes.map((devis) => (
                <div key={devis.id} className="px-6 py-4 hover:bg-beige-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-brown-900">
                        {devis.numero || devis.titre || 'Sans titre'}
                      </p>
                      {devis.client && (
                        <p className="text-sm text-brown-500">
                          {devis.client.full_name || devis.client.company}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-brown-900">
                        {formatCurrency(devis.montant_ttc || 0)}
                      </p>
                      <Badge
                        variant={
                          devis.status === 'accepted'
                            ? 'mint'
                            : devis.status === 'sent'
                            ? 'gold'
                            : 'default'
                        }
                      >
                        {devis.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Tasks Widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActiveTasksWidget />
        <OverdueTasksWidget />
      </div>
    </div>
  )
}
